import { Component, inject, OnInit, ViewChildren, QueryList } from '@angular/core';
import { ContentAreaComponent } from "src/app/components/content-area/content-area.component";
import { FormAreaComponent } from "src/app/components/form-area/form-area.component";
import { InputComponent } from "src/app/components/input/input.component";
import { SelectComponent } from "src/app/components/select/select.component";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IButtonType } from 'src/app/interfaces/components/button.interface';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TContentAreaAction } from 'src/app/interfaces/components/content-area.interface';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';
import { CardAreaComponent } from "src/app/components/card-area/card-area.component";
import { EmailCardComponent } from 'src/app/components/email-card/email-card.component';
import { AddressCardComponent } from 'src/app/components/address-card/address-card.component';
import { PersonService } from 'src/app/services/person/person.service';
import { UserService } from 'src/app/services/users/user.service';
import { AlertService } from 'src/app/services/alert.service';
import { populateFormArray } from 'src/app/utils/form-array.utils';
import { ISelectOption } from 'src/app/interfaces/components/select.interface';
import { MaskType } from 'src/app/interfaces/components/input.interface';
import { ContactsCardComponent } from '../../person/person-form/contacts-card/contacts-card.component';
import { FinancialInformationCardComponent } from '../../person/person-form/financial-information-card/financial-information-card.component';
import { ClientsService } from 'src/app/services/clients.service';
import {
  createAddressForm,
  createContactsForm,
  createEmailForm,
  createPaymentMethodsForm,
  handleDocumentFunction,
  handleIcmsTaxpayerFunction,
  validateIsMainEmail,
  validateIsMainPaymentMethod,
  validateIsMainTelephone,
} from './my-data-form.utils';
import {
  enterpriseTypeOptions,
  fiscalEnclosureOptions,
  icmsTaxpayerOptions,
 } from './my-data-form.constants';
import { DocumentUploadComponent } from "src/app/components/document-upload/document-upload.component";

@Component({
  selector: 'app-my-data-form',
  standalone: true,
  imports: [
    ContentAreaComponent,
    FormAreaComponent,
    InputComponent,
    SelectComponent,
    MatSlideToggle,
    CardAreaComponent,
    ContactsCardComponent,
    EmailCardComponent,
    FinancialInformationCardComponent,
    AddressCardComponent,
    TranslateModule,
    DocumentUploadComponent
  ],
  templateUrl: './my-data-form.component.html',
})
export class MyDataFormComponent implements OnInit {
  @ViewChildren(AddressCardComponent) addressCardComponents!: QueryList<AddressCardComponent>;
  @ViewChildren(EmailCardComponent) emailCardComponents!: QueryList<EmailCardComponent>;
  @ViewChildren(ContactsCardComponent) contactsCardComponents!: QueryList<ContactsCardComponent>;
  @ViewChildren(FinancialInformationCardComponent) financialInformationCardComponents!: QueryList<FinancialInformationCardComponent>;

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly personService = inject(PersonService);
  private readonly userService = inject(UserService);
  private readonly clientsService = inject(ClientsService);
  private readonly alertService = inject(AlertService);

  fiscalEnclosureOptions = fiscalEnclosureOptions;
  enterpriseTypeOptions = enterpriseTypeOptions;
  icmsTaxpayerOptions = icmsTaxpayerOptions;
  enterpriseOptions: ISelectOption[] = [];
  userEnterpriseIds = new Set<string>();
  relationsDisabledMap: boolean[] = [];

  id = '';

  form: FormGroup = this.fb.group({
    id: null,
    nameCorporate: ['', Validators.required],
    tradeName: ['', Validators.required],
    document: ['', Validators.required],
    enterpriseType: ['', Validators.required],
    documentType: [''],
    icmsTaxpayer: ['', Validators.required],
    stateRegistrationId: [''],
    municipalRegistration: [''],
    status: [true],
    creditLimit: ['', Validators.required],
    taxFramework: ['', Validators.required],
    paymentMethods: this.fb.array([]),
    emails: this.fb.array([]),
    addresses: this.fb.array([]),
    telephoneContacts: this.fb.array([]),
    digitalCertificate: [null, Validators.required],
    certificatePassword: [''],
    digitalCertificateId: ['']
  });

  private savedCertificateId: string | null = null;

  get telephoneContacts(): FormArray {
    return this.form.get('telephoneContacts') as FormArray;
  }

  get addresses(): FormArray {
    return this.form.get('addresses') as FormArray;
  }

  get emails(): FormArray {
    return this.form.get('emails') as FormArray;
  }

  get paymentMethods(): FormArray {
    return this.form.get('paymentMethods') as FormArray;
  }

  ngOnInit(): void {
    this.dialog.closeAll();
    this.id = sessionStorage.getItem('personId') ?? '';
    this.loadEnterprises();
    this.form.get('document')?.disable();
  }

  loadEnterprises(): void {
    this.userService.getEnterprises().subscribe((res) => {
      this.enterpriseOptions = res.data.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      this.userEnterpriseIds = new Set(res.data.map((item: any) => item.id));

      if (this.id) {
        this.initialFormValue();
        this.form.get('id')?.setValue(this.id);
      } else {
        this.addContacts();
        this.addAddress();
        this.addEmail();
      }
    });
  }

  initialFormValue() {
    this.personService.getById(this.id).subscribe((res) => {
      const data = res.data;

      this.form.patchValue({
        ...data,
        digitalCertificate: data.digitalCertificate?.fileName,
        digitalCertificateId: data.digitalCertificate?.id,
      });
      handleIcmsTaxpayerFunction(this.form);

      this.savedCertificateId = data.digitalCertificate?.id ?? null;
      this.syncCertificatePasswordRequirement();

      populateFormArray(this.emails, data.emails, (email) =>
        createEmailForm(this.fb, email, true)
      );

      populateFormArray(this.addresses, data.addresses, (address) =>
        createAddressForm(this.fb, address)
      );

      populateFormArray(this.telephoneContacts, data.telephoneContacts, (contact) =>
        createContactsForm(this.fb, contact)
      );

      populateFormArray(this.paymentMethods, data.paymentMethods, (paymentMethod) =>
        createPaymentMethodsForm(this.fb, paymentMethod)
      );

      if (this.emails.length === 0) {
        this.addEmail();
      }

      if (this.addresses.length === 0) {
        this.addAddress();
      }

      if (this.telephoneContacts.length === 0) {
        this.addContacts();
      }

      this.relationsDisabledMap = (data.relations ?? []).map(
        (relation) => !this.userEnterpriseIds.has(relation.sourceId)
      );
    });
  }

  isRelationDisabled(index: number): boolean {
    return this.relationsDisabledMap[index] ?? false;
  }

  handleDocumentChange(event: string) {
    handleDocumentFunction(event, this.form);
  }

  handleIcmsTaxpayerChange() {
    handleIcmsTaxpayerFunction(this.form);
  }

  get stateRegistrationLabel(): string {
    const documentType = this.form.get('documentType')?.value;
    if (documentType === 'PF') return 'PEOPLE_FORM.FORM.PEOPLE_DATA.PERSONAL_REGISTRATION';
    if (documentType === 'PJ') return 'PEOPLE_FORM.FORM.PEOPLE_DATA.STATE_REGISTRATION';
    return 'PEOPLE_FORM.FORM.PEOPLE_DATA.RG';
  }

  get stateRegistrationPlaceholder(): string {
    return this.form.get('documentType')?.value === 'PJ'
      ? 'PEOPLE_FORM.FORM.PEOPLE_DATA.STATE_REGISTRATION_PLACEHOLDER'
      : 'PEOPLE_FORM.FORM.PEOPLE_DATA.RG_PLACEHOLDER';
  }

  get stateRegistrationMask(): MaskType {
    return this.form.get('documentType')?.value === 'PF' ? 'rg' : '';
  }

  handleContentAreaEvent(event: TContentAreaAction) {
    if (event === 'back') {
      this.cancelEvent();
    }
  }

  handleFormAreaEvent(event: IButtonType) {
    if (event === 'save') {
      this.submitForm();
    }
    if (event === 'cancel') {
      this.cancelEvent();
    }
  }

  back() {
    this.route.navigate(['/panel/person']);
  }

  cancelEvent() {
    if (this.form.touched) {
      this.dialog.open(ModalComponent, {
        data: {
          type: 'cancel',
        },
      })
      .afterClosed().subscribe((confirm) => {
        if (confirm) {
         this.back();
        }
      });
    } else {
      this.back();
    }
  }

  private buildAdjustDataPayload() {
    const payload = this.form.getRawValue();
    return payload;
  }

  private isCertificateChanged(): boolean {
    const current = this.form.get('digitalCertificateId')?.value ?? null;
    return !this.id || current !== this.savedCertificateId;
  }

  private syncCertificatePasswordRequirement() {
    const control = this.form.get('certificatePassword');
    if (!control) return;

    if (this.isCertificateChanged()) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  submitForm() {
    this.syncCertificatePasswordRequirement();
    this.form.markAllAsTouched();

    if (this.form.valid) {
      if (validateIsMainEmail(this.emailCardComponents) === false) {
        this.alertService.show({
          type: 'alert',
          subtitle: 'PEOPLE_FORM.MODAL.MAIN_EMAIL_REQUIRED'
        });
        return;
      }

      if (validateIsMainTelephone(this.contactsCardComponents) === false) {
        this.alertService.show({
          type: 'alert',
          subtitle: 'PEOPLE_FORM.MODAL.MAIN_TELEPHONE_REQUIRED'
        });
        return;
      }

      if (this.paymentMethods.length !== 0) {
        if (validateIsMainPaymentMethod(this.financialInformationCardComponents) === false) {
          this.alertService.show({
            type: 'alert',
            subtitle: 'PEOPLE_FORM.MODAL.MAIN_PAYMENT_METHOD_REQUIRED'
          });
          return;
        }
      }

      this.clientsService.adjustData(this.buildAdjustDataPayload()).subscribe({
        next: () => {
          this.alertService.show({
            type: 'success',
            subtitle: 'PEOPLE_FORM.MODAL.SUCCESS_MESSAGE'
          });
          window.location.reload();
        },
        error: (error) => {
          this.alertService.show({
            type: 'error',
            subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
          });
        }
      });
    } else {
      this.addressCardComponents.forEach(component => {
        component.isFormInvalid();
      });
      this.emailCardComponents.forEach(component => {
        component.isFormInvalid();
      });
      this.contactsCardComponents.forEach(component => {
        component.isFormInvalid();
      });
      this.financialInformationCardComponents.forEach(component => {
        component.isFormInvalid();
      });
      this.alertService.show({
        type: 'alert',
        subtitle: 'ALERT_MODAL.DEFAULT_MESSAGE_ALERT'
      });
    }
  }

  addContacts(): void {
    if (this.telephoneContacts.length === 0 || this.form.get('telephoneContacts')?.valid) {
      this.telephoneContacts.push(createContactsForm(this.fb));
    } else {
      this.telephoneContacts.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }

  removeContacts(index: number): void {
    this.telephoneContacts.removeAt(index);
  }

  addAddress(): void {
    if (this.addresses.length === 0 || this.form.get('addresses')?.valid) {
      this.addresses.push(createAddressForm(this.fb));
    } else {
      this.addresses.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }

  removeAddress(index: number): void {
    this.addresses.removeAt(index);
  }

  addEmail(): void {
    if (this.emails.length === 0 || this.form.get('emails')?.valid) {
      this.emails.push(createEmailForm(this.fb));
    } else {
      this.emails.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }

  removeEmail(index: number): void {
    this.emails.removeAt(index);
  }

  addPaymentMethods(): void {
    if (this.paymentMethods.length === 0 || this.form.get('paymentMethods')?.valid) {
      this.paymentMethods.push(createPaymentMethodsForm(this.fb));
    } else {
      this.paymentMethods.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }

  removePaymentMethods(index: number): void {
    this.paymentMethods.removeAt(index);
  }
}
